// Who can see a case, and how a staff-opened ticket is asked for.
//
// A customer finds their cases BY ContactId — the dashboard card, the ticket
// routes, the assistant's case tools all key on it (squish-backend). So a case
// is one of:
//
//   customer — linked to a Contact: on their dashboard, replies reach them
//   silent   — Staff_Only__c: deliberately unlinked; they never see it
//   unlinked — has an account but no Contact, and is NOT silent: an orphan.
//              Nobody can see it and nobody meant that (#00001057, 2026-10-05)
//   web-form — the public help form: no account, a SuppliedEmail to answer
//   none     — nothing to reach
//
// `staffOnly` comes from GET /api/cases/:id. It is null until
// Case.Staff_Only__c is deployed, and a silent ticket then reads as unlinked —
// the safe misreading (it offers a link, it never claims the customer can see).

export function caseAudience(c, staffOnly) {
  if (!c) return { kind: 'none', canLink: false };
  if (c.ContactId) return { kind: 'customer', canLink: false };
  if (staffOnly === true) return { kind: 'silent', canLink: Boolean(c.AccountId) };
  if (c.AccountId) return { kind: 'unlinked', canLink: true };
  return { kind: c.SuppliedEmail ? 'web-form' : 'none', canLink: false };
}

export const TICKET_VISIBILITIES = ['customer', 'staff'];

// The body for POST /api/accounts/:id/cases. `visibility` is what routes it
// through squish-crm-backend lib/staff-case.js; without it the route takes the
// legacy path that links nobody. The one text box means different things per
// audience: a message TO the customer on a visible ticket, a staff note on a
// silent one. Never a Description — that renders as the customer's own words.
export function newTicketBody({ subject, priority = 'Medium', visibility, contactId = null, message = '' }) {
  if (!TICKET_VISIBILITIES.includes(visibility)) throw new Error('Choose who sees this ticket.');
  const subj = String(subject || '').trim();
  if (!subj) throw new Error('Give the ticket a subject.');
  const text = String(message || '').trim() || undefined;
  return visibility === 'customer'
    ? { subject: subj, priority, visibility, contactId: contactId || null, customerMessage: text }
    : { subject: subj, priority, visibility, internalNote: text };
}

// The contact a visible ticket defaults to: the account's only one. With
// several, nobody guesses — the rep picks (and the backend refuses a guess too).
export function defaultContactId(contacts) {
  const list = (contacts || []).filter(c => c && c.Id);
  return list.length === 1 ? list[0].Id : '';
}

export function contactLabel(c) {
  if (!c) return '';
  const name = c.name || c.Name || [c.FirstName, c.LastName].filter(Boolean).join(' ');
  const email = c.email || c.Email;
  return [name || null, email ? `<${email}>` : null].filter(Boolean).join(' ') || c.id || c.Id || '';
}

// "Opened" and "the customer was told" are different facts; say which.
export function deliveryText(r) {
  if (!r || r.emailed === undefined) return '';
  return r.emailed ? `emailed to ${r.to}` : `not emailed (${r.reason || 'unknown reason'})`;
}

// Close files the composer as an INTERNAL resolution record. On a case the
// customer can see, closing that way tells them nothing, so the confirm says
// so and points at Send & close — the path that sends the resolution.
export function closeConfirmText(customerCanSee) {
  return customerCanSee
    ? 'Close without telling the customer?\n\nThe note below is filed internally. To send them the resolution, switch to "Reply to customer" and use Send & close.'
    : 'Close this case with the resolution note below?';
}

// How a staff reply marked "customer" reaches a human, if at all.
//
//   dashboard — the case has a Contact: their dashboard thread shows it and
//               squish-crm-backend emails Contact.Email too
//   email     — no Contact, but a SuppliedEmail (the public help / sales form):
//               there is no dashboard thread, but the backend's reply mailer
//               falls back to SuppliedEmail, so the words still arrive
//   none      — nothing to reach, or a silent ticket (emailing a Staff_Only__c
//               case would break the silence it was filed with)
//
// The composer used to gate on ContactId alone (#00001159, 2026-11-02): every
// sales inquiry from someone who is not yet a customer arrived with an email
// and no Contact, and the one button that answers it was greyed out while the
// backend would have mailed them fine.
export function customerReach(c, staffOnly) {
  const a = caseAudience(c, staffOnly);
  if (a.kind === 'customer') return 'dashboard';
  if (a.kind === 'silent' || a.kind === 'none') return 'none';
  return c?.SuppliedEmail ? 'email' : 'none';
}
